import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Divider,
    ListSubheader,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import AutoComplete from '@mui/material/Autocomplete';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import { UPDATE_VALIDATION_RULE } from '../../../store/ruleSet';
import { JobTransferValidationType, getJobTransferValidationTypeLabel, RequestTypes, RuleSetType } from '../../../../Enums';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';
import StyledSwitch from '../StyledSwitch';
import ActualHours from '../Step3-ScheduleRulesSection/ActualHours';
import OnRestrictionSection from './OnRestrictionSection';
import OnProbationSection from './OnProbationSection';
import './Step2-EmployeeRulesSection.scss';

/**
 * @param {{ validationErrors: {[Key: string], isDisabled: boolean }, stepNumber: number }} props
 */
export default function EmployeeRulesSection(props) {
    const { validationErrors, isDisabled, stepNumber, } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet' });
    const dispatch = useDispatch();
    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    /** @type {AdminAppData.IAppData} */
    const adminAppData = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminAppData);
    const { requestType, validationRuleSet } = adminRuleSet;
    const { jobTransferValidationType, isSkills, isCertifications, isEmployeeEligibility, employeeEligibilityLength,
        employeeEligibilityPeriod, employeeEligibilityCustomDays, employeeEligibilityDataviewId, employeeEligibilityDataviewKeyAlias,
        employeeEligibilityIsUseActualHours, employeeEligibilityBreakRule,
    } = validationRuleSet;
    const { dataviews, symbolicPeriods } = adminAppData;
    const breakRules = adminAppData.ruleSets.filter(x => x.ruleSetType === RuleSetType.BreakRule);
    const isVto = requestType === RequestTypes.voluntaryTimeOff;
    
    /**
     * @typedef {{value: JobTransferValidationType|null, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const jobTransferTypeOptions = [
        { value: null, label: t('employeeRulesStep.none') }, 
        ...Object.values(JobTransferValidationType).map((x) => ({ value: x, label: getJobTransferValidationTypeLabel(x) }))];

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const eligibilityDataviewFields = dataviews.find((x) => x.id === employeeEligibilityDataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
    })) || [];

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    const SHIFT_REL_PREFIX = 'SHIFT_REL_';
    const SHIFT_REL_LAST_CUSTOM_PREFIX = 'SHIFT_REL_LAST_CUSTOM_';
    const SHIFT_REL_NEXT_CUSTOM_PREFIX = 'SHIFT_REL_NEXT_CUSTOM_';
    const ukgPeriods = symbolicPeriods.filter((x) => !x.id.startsWith(SHIFT_REL_PREFIX));
    const shiftRelativePeriods = symbolicPeriods.filter((x) => x.id.startsWith(SHIFT_REL_PREFIX));
    const isShiftRelativePeriod = employeeEligibilityPeriod?.startsWith(SHIFT_REL_PREFIX);

    const isCustomPeriod = employeeEligibilityPeriod === SHIFT_REL_LAST_CUSTOM_PREFIX
        || employeeEligibilityPeriod === SHIFT_REL_NEXT_CUSTOM_PREFIX;

    /** @param {AdminAppData.ISymbolicPeriod} period */
    const getPeriodLabel = (period) => {
        if (!period.id.startsWith(SHIFT_REL_PREFIX)) return period.name;
        const translationKey = `employeeRulesStep.shiftRelativePeriods.${period.id}`;
        const isCustom = period.id === SHIFT_REL_LAST_CUSTOM_PREFIX || period.id === SHIFT_REL_NEXT_CUSTOM_PREFIX;
        return isCustom ? t(translationKey, { days: employeeEligibilityCustomDays || '' }) : t(translationKey);
    };

    /** Returns the display label for the currently selected period */
    const getSelectedPeriodLabel = () => {
        if (!employeeEligibilityPeriod) return '';
        if (isCustomPeriod) {
            const baseKey = `employeeRulesStep.shiftRelativePeriods.${employeeEligibilityPeriod}`;
            return t(baseKey, { days: employeeEligibilityCustomDays || '' });
        }
        const found = symbolicPeriods.find((x) => x.id === employeeEligibilityPeriod);
        return found ? getPeriodLabel(found) : '';
    };

    const selectedJobTransferType = jobTransferTypeOptions.find((x) => x.value === jobTransferValidationType);
    const selectedEmployeeEligibilityPeriod = symbolicPeriods.find((x) => x.id === employeeEligibilityPeriod);
    const selectedEligibilityDataviewKeyAlias = eligibilityDataviewFields.find((x) => x.value === employeeEligibilityDataviewKeyAlias);

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                titleTypographyProps={{ gutterBottom: true }}
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Employee rules"
            />
            <CardContent className="ers-cardContent no-gap">
                {!isVto && (
                    <>
                        <InputTitle>Qualification</InputTitle>
                        <StyledSelect
                            labelId="select-jobTransfer-label"
                            id="select-jobTransfer-type"
                            disabled={isDisabled}
                            displayEmpty
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedJobTransferType?.label}
                            value={selectedJobTransferType?.value ?? ''}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('jobTransferValidationType', event.target.value)}
                        >
                            {jobTransferTypeOptions.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.jobTransferValidationType ? <span style={{ color: 'red' }}>Please select a validation type</span> : null
                        }
                        <StyledSwitch
                            checked={isSkills}
                            onChange={(value) => updateValidationRulesStore('isSkills', value)}
                            label={'Skills'}
                            disabled={isDisabled}
                        />
                        <StyledSwitch
                            checked={isCertifications}
                            onChange={(value) => updateValidationRulesStore('isCertifications', value)}
                            label={'Certifications'}
                            disabled={isDisabled}
                        /></>)}
                <OnProbationSection {...props} />
                <OnRestrictionSection {...props} />
                <div>
                    <StyledSwitch
                        checked={isEmployeeEligibility}
                        onChange={(value) => updateValidationRulesStore('isEmployeeEligibility', value)}
                        label={'Employee eligibility based on hours worked'}
                        disabled={isDisabled}
                    />
                    {isEmployeeEligibility && (<>
                        <NumberInput
                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                            min={1}
                            max={999}
                            step={1}
                            precision={0}
                            value={employeeEligibilityLength}
                            defaultValue={1}
                            target={'employeeEligibilityLength'}
                            isDisabled={isDisabled}
                            error={validationErrors.employeeEligibilityLength || (isEmployeeEligibility && employeeEligibilityLength && employeeEligibilityLength < 1)}
                        />
                        <Typography
                            variant="body1"
                            className="ers-numeric-input-unit-label"
                        >
                            Hours during the following period
                        </Typography>
                        <StyledSelect
                            labelId="select-symbolid-period-label"
                            id="select-symbolid-period-type"
                            disabled={isDisabled}
                            renderValue={() => getSelectedPeriodLabel()}
                            value={selectedEmployeeEligibilityPeriod?.id ?? ''}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => {
                                const value = event.target.value;
                                updateValidationRulesStore('employeeEligibilityPeriod', value);
                                if (value === SHIFT_REL_LAST_CUSTOM_PREFIX || value === SHIFT_REL_NEXT_CUSTOM_PREFIX) {
                                    if (!employeeEligibilityCustomDays) {
                                        updateValidationRulesStore('employeeEligibilityCustomDays', 1);
                                    }
                                } else {
                                    updateValidationRulesStore('employeeEligibilityCustomDays', null);
                                }
                            }}
                            style={{ width: '370px', display: 'inline-grid' }}
                        >
                            {ukgPeriods.length > 0 && (
                                <ListSubheader disableSticky style={{ color: 'black', fontWeight: 'bold', fontSize: '1rem' }}>
                                    {t('employeeRulesStep.periodGroupRelativeToToday')}
                                </ListSubheader>
                            )}
                            {ukgPeriods.map((x) => (
                                <MenuItem value={x.id} key={`SelectItem_${x.id}`}>
                                    {x.name}
                                </MenuItem>
                            ))}
                            {!isVto && shiftRelativePeriods.length > 0 && ukgPeriods.length > 0 && (
                                <Divider />
                            )}
                            {!isVto && shiftRelativePeriods.length > 0 && (
                                <ListSubheader disableSticky style={{ color: 'black', fontWeight: 'bold', fontSize: '1rem' }}>
                                    {t('employeeRulesStep.periodGroupRelativeToShift')}
                                </ListSubheader>
                            )}
                            {!isVto && shiftRelativePeriods.map((x) => (
                                <MenuItem value={x.id} key={`SelectItem_${x.id}`}>
                                    {getPeriodLabel(x)}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {isCustomPeriod && (
                            <span style={{ marginLeft: '10px', display: 'inline-block' }}>
                                <NumberInput
                                    handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                    min={1}
                                    max={90}
                                    step={1}
                                    precision={0}
                                    value={employeeEligibilityCustomDays || 1}
                                    defaultValue={1}
                                    target={'employeeEligibilityCustomDays'}
                                    isDisabled={isDisabled}
                                    unitType={t('employeeRulesStep.days')}
                                />
                            </span>
                        )}
                    </>)}
                </div>
                {
                    validationErrors.employeeEligibilityLength ? <span style={{ color: 'red' }}>The number of hours must be greater than 0</span> : null
                }
                {
                    validationErrors.employeeEligibilityPeriod ? <span style={{ color: 'red', display: 'flex' }}>Please select a period</span> : null
                }
                {isEmployeeEligibility && isShiftRelativePeriod && (
                    <div>
                        <ActualHours
                            isActualHours={employeeEligibilityIsUseActualHours}
                            setIsActualHours={(isChecked) => updateValidationRulesStore('employeeEligibilityIsUseActualHours', isChecked)}
                            breakRuleId={employeeEligibilityBreakRule}
                            setBreakRuleId={(id) => updateValidationRulesStore('employeeEligibilityBreakRule', id)}
                            breakRules={breakRules}
                            helpIconTooltip={t('employeeRulesStep.useActualHoursTooltip')}
                            isDisabled={isDisabled}
                        />
                    </div>)}
                {isEmployeeEligibility && !isShiftRelativePeriod && (
                    <div className="ers-dataview-info-container">
                        <div className="rse--selection-field-container ers-one-half-space">
                            <InputTitle>Hours calculated with this dataview:</InputTitle>
                            <AutoComplete
                                id="dataview-select"
                                value={dataviews.find((x) => x.id === employeeEligibilityDataviewId) ?? null}
                                options={dataviews}
                                onChange={(_event, optionSelected) => updateValidationRulesStore('employeeEligibilityDataviewId', optionSelected?.id)}
                                renderInput={(params) => (<TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder="Select a dataview"
                                />)}
                                getOptionLabel={(option) => option.name}
                                includeInputInList
                                disabled={isDisabled}
                                disableClearable
                            />
                        </div>
                        <div className="rse--selection-field-container ers-one-half-space">
                            <InputTitle>Field</InputTitle>
                            <StyledSelect
                                labelId="select-dataview-field-label"
                                id="select-dataview-field-type"
                                disabled={isDisabled}
                                renderValue={(/** @type {string} */
                                    _value,
                                ) => selectedEligibilityDataviewKeyAlias?.label}
                                value={selectedEligibilityDataviewKeyAlias?.value ?? ''}
                                onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                    event,
                                ) => updateValidationRulesStore('employeeEligibilityDataviewKeyAlias', event.target.value)}
                            >
                                {eligibilityDataviewFields.map((x) => (
                                    <MenuItem
                                        value={x.value}
                                        key={`SelectItem_${x.label}`}
                                    >
                                        {x.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </div>
                    </div>)}
                {
                    validationErrors.employeeEligibilityDataviewId || validationErrors.employeeEligibilityDataviewKeyAlias ? (
                        <span style={{ color: 'red' }}>Please select a dataview and dataview field</span>) : null
                }
            </CardContent>
        </Card>

    );
}
